// src/dashboard/TherapistProfile.js
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRef } from 'react';
import { User, Award, Mail, Phone, MapPin, Edit, Save, X } from 'lucide-react';


const TherapistProfile = ({ user }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const requestInProgressRef = useRef(false);

  const [formData, setFormData] = useState({
    
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    experience: '',
    specialty: '',
    bio: '',
    address: '',
    insightsApplied: [{
        insightText: String,
        relevance: String,
        source: {
          type: String,
          enum: ['rick', 'dave'],
          default: 'unknown'
        }
      }],
    analysisDetails: {
    type: Object,
    default: {}
    },
    userFriendlyDescription: {
        type: String,
        default: ''
      },
      descriptionGenerated: {
        type: Boolean,
        default: false
      },
  });
  
  const navigate = useNavigate();

  const [description, setDescription] = useState('');
  const [loadingDescription, setLoadingDescription] = useState(false);
  
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        // Try to find existing profile
        const response = await fetch(`/api/therapist-profile/${user.id}`);
        
        if (response.ok) {
          const data = await response.json();
          setProfile(data.profile);
          
          // If profile has a description, use it directly (avoids a second request)
          if (data.profile && data.profile.userFriendlyDescription) {
            console.log("Setting description from profile fetch");
            setDescription(data.profile.userFriendlyDescription);
          }
          
          // Initialize form data with profile values
          setFormData({
            firstName: data.profile.firstName || user.firstName || '',
            lastName: data.profile.lastName || user.lastName || '',
            email: data.profile.email || user.email || '',
            phone: data.profile.phone || '',
            experience: data.profile.experience || '',
            specialty: data.profile.specialty || '',
            bio: data.profile.bio || '',
            address: data.profile.address || ''
          });
        } else {
          // If no profile found, initialize with user data
          setProfile(null);
          setFormData({
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
            phone: '',
            experience: '',
            specialty: '',
            bio: '',
            address: ''
          });
        }
      } catch (error) {
        console.error('Error fetching therapist profile:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfile();
  }, [user]);

  useEffect(() => {
    let isMounted = true;
  
    const fetchDescription = async () => {
      // Don't fetch if there's no user or already loading
      if (!user || loadingDescription || !isMounted) return;
      
      // Only skip if we already have a non-empty description
      if (description && description.trim().length > 0) {
        console.log("Already have description:", description.substring(0, 20));
        return;
      }
      
      console.log("Starting description fetch for user:", user.id);
      setLoadingDescription(true);
      
      try {
        const response = await fetch(`/api/generate-therapist-description/${user.id}`, {
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        });
        
        console.log("Description API status:", response.status);
        
        if (response.ok && isMounted) {
          const data = await response.json();
          console.log("Description API response keys:", Object.keys(data));
          
          if (data.description) {
            console.log("Setting description:", data.description.substring(0, 20) + "...");
            setDescription(data.description);
          } else {
            console.error("No description in response data:", data);
          }
        }
      } catch (error) {
        console.error('Error fetching description:', error);
      } finally {
        if (isMounted) {
          setLoadingDescription(false);
        }
      }
    };
    
    fetchDescription();
    
    return () => {
      isMounted = false;
    };
  }, [user?.id]);


  const [saving, setSaving] = useState(false);
  
  
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Toggle edit mode
  const toggleEdit = () => {
    if (editing) {
      // If cancelling edit, reset form data
      setFormData({
        firstName: profile?.firstName || user?.firstName || '',
        lastName: profile?.lastName || user?.lastName || '',
        email: profile?.email || user?.email || '',
        phone: profile?.phone || '',
        experience: profile?.experience || '',
        specialty: profile?.specialty || '',
        bio: profile?.bio || '',
        address: profile?.address || ''
      });
    }
    setEditing(!editing);
  };
  
  // Save profile changes
  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!user) return;
    
    try {
      setSaving(true);
      
      const response = await fetch(`/api/therapist-profile/${user.id}`, {
        method: profile ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to save profile');
      }
      
      const data = await response.json();
      setProfile(data.profile);
      setEditing(false);
      
      // Show success message
      alert('Profile saved successfully!');
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Error saving profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  return (
    <div>
      {!editing ? (
        // View Mode
        <div>
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-800">Personal Information</h3>
            <button
              onClick={toggleEdit}
              className="flex items-center px-3 py-1.5 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200"
            >
              <Edit size={16} className="mr-1" />
              Edit Profile
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 p-2 rounded-full mr-3">
                  <User className="text-blue-600" size={24} />
                </div>
                <div>
                  <div className="text-sm text-gray-500">Full Name</div>
                  <div className="font-medium">
                    Dr. {formData.firstName} {formData.lastName}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 p-2 rounded-full mr-3">
                  <Mail className="text-blue-600" size={24} />
                </div>
                <div>
                  <div className="text-sm text-gray-500">Email</div>
                  <div className="font-medium">{formData.email}</div>
                </div>
              </div>
              
              {formData.phone && (
                <div className="flex items-center mb-4">
                  <div className="bg-blue-100 p-2 rounded-full mr-3">
                    <Phone className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Phone</div>
                    <div className="font-medium">{formData.phone}</div>
                  </div>
                </div>
              )}
              
              {formData.address && (
                <div className="flex items-center mb-4">
                  <div className="bg-blue-100 p-2 rounded-full mr-3">
                    <MapPin className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Address</div>
                    <div className="font-medium">{formData.address}</div>
                  </div>
                </div>
              )}
            </div>
            
            <div>
              <div className="flex items-center mb-4">
                <div className="bg-blue-100 p-2 rounded-full mr-3">
                  <Award className="text-blue-600" size={24} />
                </div>
                <div>
                  <div className="text-sm text-gray-500">Experience</div>
                  <div className="font-medium">
                    {formData.experience ? `${formData.experience} years` : 'Not specified'}
                  </div>
                </div>
              </div>
              
              {formData.specialty && (
                <div className="mb-4">
                  <div className="text-sm text-gray-500 mb-1">Specialty</div>
                  <div className="font-medium">{formData.specialty}</div>
                </div>
              )}
            </div>
          </div>
          
          {formData.bio && (
            <div className="mt-4">
              <div className="text-sm text-gray-500 mb-1">Bio</div>
              <div className="p-4 bg-gray-50 rounded-md">
                {formData.bio}
              </div>
            </div>
          )}
          {profile && profile.insightsApplied && profile.insightsApplied.length > 0 && (
            <div className="mt-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">
                Expert Insights Applied to Your Profile
                </h3>
                <div className="bg-purple-50 border border-purple-200 rounded-md p-4">
                <p className="text-sm text-purple-800 mb-3">
                    The following expert insights from our senior therapists were identified as relevant to your profile based on your questionnaire responses:
                </p>
                <div className="space-y-3">
                    {profile.insightsApplied.map((insight, index) => (
                    <div key={index} className="bg-white p-3 rounded-md shadow-sm">
                        <div className="font-medium text-purple-700">
                        From Dr. {insight.source.charAt(0).toUpperCase() + insight.source.slice(1)}:
                        </div>
                        <div className="mt-1 text-gray-700">"{insight.insightText}"</div>
                        <div className="mt-2 text-sm text-gray-600">
                        <span className="font-medium">Relevance:</span> {insight.relevance}
                        </div>
                    </div>
                    ))}
                </div>
                </div>
            </div>
            )}
            {!editing && (
        <div className="mt-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Your Therapeutic Style</h3>
          
          {loadingDescription ? (
            <div className="p-6 border border-gray-200 rounded-md bg-gray-50 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-3"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6 mb-3"></div>
              <div className="h-4 bg-gray-200 rounded w-4/5 mb-3"></div>
              <div className="h-4 bg-gray-200 rounded w-full mb-3"></div>
            </div>
          ) : description ? (
            <div className="p-6 border border-blue-200 rounded-md bg-blue-50">
              <div className="prose prose-blue max-w-none">
                {description.split('\n\n').map((paragraph, i) => (
                  <p key={i} className="mb-4 last:mb-0">{paragraph}</p>
                ))}
              </div>
            </div>
          ) : (
            <div className="p-6 border border-gray-200 rounded-md bg-gray-50">
              <p className="text-gray-500 italic mb-4">
                Complete your profile to see a personalized description of your therapeutic style.
              </p>
              <button
                onClick={() => navigate('/therapist-questionnaire')}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Complete Questionnaire
              </button>
            </div>
          )}
        </div>
      )}
        </div>
        
      ) : (
        // Edit Mode
        <form onSubmit={handleSave}>
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-gray-800">Edit Profile</h3>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={toggleEdit}
                className="flex items-center px-3 py-1.5 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                disabled={saving}
              >
                <X size={16} className="mr-1" />
                Cancel
              </button>
              <button
                type="submit"
                className="flex items-center px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-400"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} className="mr-1" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                First Name*
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              />
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                Last Name*
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email*
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-1">
                Years of Experience*
              </label>
              <input
                type="number"
                id="experience"
                name="experience"
                value={formData.experience}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md"
                min="0"
                max="70"
                required
              />
            </div>
            <div>
              <label htmlFor="specialty" className="block text-sm font-medium text-gray-700 mb-1">
                Specialty
              </label>
              <input
                type="text"
                id="specialty"
                name="specialty"
                value={formData.specialty}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="e.g., Cognitive Behavioral Therapy"
              />
            </div>
          </div>
          
          <div className="mb-4">
            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
              Office Address
            </label>
            <input
              type="text"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md"
              placeholder="123 Main St, Anytown, USA"
            />
          </div>
          
          <div className="mb-4">
            <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-1">
              Professional Bio
            </label>
            <textarea
              id="bio"
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md min-h-32"
              placeholder="Tell clients about your background, approach, and specialties..."
            ></textarea>
          </div>
          
          <p className="text-sm text-gray-500 italic">
            Fields marked with * are required.
          </p>
        </form>
      )}
    </div>
  );
};

export default TherapistProfile;